/**
 * The multiplatform IA, single source of truth (CO 032-001, §3).
 *
 * CO 071: the TENANT is the spine (identity/accounts/agents/governance/voice/
 * knowledge). Every platform composes on top of it — no platform is privileged.
 *
 * CO 109 posture distinction (the load-bearing one for a non-retail visitor):
 *  - VENDOR platforms = what your business IS / what you sell on. A *kind of
 *    business*: a store (Retail), a dentist (Bookings), a kitchen (Food), an
 *    auto shop (Services). This is where a non-retail visitor recognizes itself.
 *  - CUSTOMER platforms = TOOLS you use to run it (Email, Shipping, Marketing,
 *    Payments, Customer Service). NOT "what your business is."
 * Keep the two separate everywhere — mixing a tool (Email) into the "kinds of
 * business" list is the exact confusion this model exists to prevent.
 *
 * Source of truth for posture/status: the federation platform registry
 * (src/search/seed-data.ts SEED_PLATFORMS / federation_catalog_platforms) —
 * the control-app catalog it previously mirrored was decommissioned (CO 307).
 * Roster reconciled to the registry 2026-09-06: 19 platforms (5 vendor + 14
 * customer) + 10 presets — workflows, consent, and recommendations added; a
 * preset is never counted in a platform total (CO 455).
 * Statuses verified 2026-08-17 (CO 455): retail, bookings, services (CO 203-002),
 * rentals (CO 203-001), digital (CO 204) available; events + food are PRESETS
 * (CO 203) — recipes composing live platforms (events = bookings + retail;
 * food = retail + bookings + shipping), composable today, never platforms, and
 * never counted in a platform total. Email → Messaging (CO 123); Payments is
 * NOT a platform (CO 124 — the tenant spine's Accounts face); Marketing
 * dissolved into offer-bus + reputation (CO 172). This file drives the nav
 * dropdowns, the platforms band, the PlatformsPage, and the detail page.
 */

export type PlatformPosture = "vendor" | "customer";
export type PlatformStatus = "available" | "coming-soon" | "preset";

export interface Platform {
  slug: string;
  name: string;
  tagline: string;
  /** One-paragraph description, written operator-to-operator. */
  blurb: string;
  /** First-class capabilities — what this platform actually does. */
  capabilities: string[];
  /** vendor = a kind of business you become; customer = a tool you use. (CO 109) */
  posture: PlatformPosture;
  /** Joinable today, or named-but-surface-pending. (CO 109) */
  status: PlatformStatus;
  /** Material icon name resolved in components, kept as data here. */
  icon: string;
  /** "Instead of stitching X + Y + Z" — what this replaces in the legacy stack. */
  replaces?: string[];
  /** A single concrete example of an agent call you can make against it. */
  agentCall?: string;
}

export const PLATFORMS: Platform[] = [
  /* ── VENDOR platforms — what your business IS / sells on ─────────────── */
  {
    slug: "retail",
    name: "Retail",
    tagline: "A store — products, a catalog, cart, and checkout.",
    blurb:
      "Catalog, cart, checkout, orders, inventory, fulfillment — the full commerce engine, exposed as a platform a tenant composes. The most common first platform a business adds, but not a requirement: a tenant is a tenant before it sells anything.",
    capabilities: [
      "Catalog + live inventory",
      "Cart, checkout, orders",
      "Agent-callable storefront (MCP / UCP / REST)",
      "Autonomous commerce agents",
    ],
    posture: "vendor",
    status: "available",
    icon: "Storefront",
    replaces: ["Shopify", "WooCommerce", "BigCommerce"],
    agentCall: "catalog.search({ query: 'wireless headphones', limit: 5 })",
  },
  {
    slug: "bookings",
    name: "Bookings",
    tagline: "Sell time — appointments, availability, intake.",
    blurb:
      "Providers, availability rules, and bookings as a platform a tenant composes to sell its time. Every salon, clinic, trainer, and consultant is a vendor here — search a service, hold a slot, confirm. Same tenant identity, same Com meter as every platform.",
    capabilities: [
      "Providers + services + availability",
      "Hold / confirm / reschedule / cancel",
      "Intake forms per service",
      "Agent-callable booking API",
    ],
    posture: "vendor",
    status: "available",
    icon: "EventAvailable",
    replaces: ["Calendly", "Acuity", "Square Appointments"],
    agentCall: "availability_get({ providerId, serviceId, from, to })",
  },
  {
    slug: "events",
    name: "Events",
    tagline: "Sell admissions — a preset composing bookings + retail.",
    blurb:
      "A preset, not a platform: a ticket is a retail item whose inventory is a bookings seat, so an events business composes the bookings platform (the seat map, capacity as a hard ceiling) with retail (the ticket as an item) — both live today. Classes, workshops, venues, conferences, and fundraisers run this recipe.",
    capabilities: [
      "Composes: bookings + retail",
      "Capacity-gated admissions (bookings slots)",
      "Tickets as catalog items (retail)",
      "Agent-callable via the composed platforms",
    ],
    posture: "vendor",
    status: "preset",
    icon: "ConfirmationNumber",
    replaces: ["Eventbrite", "Ticketmaster", "Luma"],
    agentCall: "booking_hold({ serviceId, slot })",
  },
  {
    slug: "rentals",
    name: "Rentals",
    tagline: "Sell the use of an asset — by the span, and it comes back.",
    blurb:
      "Returnable, calendar-gated, condition-tracked assets sold by the span as a platform. Tool and equipment hire, party rentals, vehicle and venue hire, gear libraries — every rental business is a vendor. The asset re-enters availability on return; deposits hold and resolve across the round trip.",
    capabilities: [
      "Per-asset availability calendar",
      "Checkout by span (start → end)",
      "Returns, deposits, condition",
      "Agent-callable rental API",
    ],
    posture: "vendor",
    status: "available",
    icon: "Inventory2",
    replaces: ["Booqable", "Rentle", "EZRentOut"],
    agentCall: "rental_checkout({ assetId, start, end })",
  },
  {
    slug: "food",
    name: "Food",
    tagline: "Sell made-to-order — a preset composing retail + bookings + shipping.",
    blurb:
      "A preset, not a platform: a menu item is a retail item, kitchen throughput is bookings capacity, and delivery is shipping — so a food business composes retail + bookings + shipping, all three live today. Restaurants, cafés, ghost kitchens, bakeries, food trucks, and caterers run this recipe; scarcity is throughput, not stock.",
    capabilities: [
      "Composes: retail + bookings + shipping",
      "Menu items as catalog items (retail)",
      "Kitchen throughput as capacity slots (bookings)",
      "Delivery dispatch (shipping)",
    ],
    posture: "vendor",
    status: "preset",
    icon: "Restaurant",
    replaces: ["Toast", "Square for Restaurants", "ChowNow"],
    agentCall: "order_create({ items, fulfillment: 'pickup' })",
  },
  {
    slug: "services",
    name: "Services",
    tagline: "Sell custom work — scope first, then price.",
    blurb:
      "Custom-priced scoped jobs as a platform — request, estimate, accept, milestone, progress-invoice. Contractors, trades, agencies, freelancers, and consultants are the vendors. The non-catalog sell: you can't post a fixed price because every job differs, so settlement fires per milestone, not once at checkout.",
    capabilities: [
      "Capability + scope templates (price TBD)",
      "Quote → revise → accept",
      "Milestones + progress invoices",
      "Agent-callable quote + job API",
    ],
    posture: "vendor",
    status: "available",
    icon: "Engineering",
    replaces: ["Jobber", "ServiceTitan", "Bonsai"],
    agentCall: "quote_submit({ requestId, lineItems, total })",
  },
  {
    slug: "digital",
    name: "Digital",
    tagline: "Sell a license or digital good — infinite supply, no shipping.",
    blurb:
      "Digital goods sold as term-bound entitlements against infinite supply — software licenses, downloads, media, templates. Issue a grant, verify it, revoke it; the entitlement ledger is the product. No inventory, no shipping. Same tenant identity, same Com meter as every platform.",
    capabilities: [
      "License + entitlement issuance",
      "Verify / revoke entitlements",
      "Term-bound grants against infinite supply",
      "Agent-callable digital API",
    ],
    posture: "vendor",
    status: "available",
    icon: "CloudDownload",
    replaces: ["Gumroad", "Lemon Squeezy", "Paddle"],
    agentCall: "entitlement_issue({ sku, term: '1y' })",
  },

  /* ── PRESETS (CO 456) — recipes composing live platforms, never platforms ── */
  {
    slug: "salon",
    name: "Salon & Spa",
    tagline: "Sell appointments that upsell goods — a preset composing bookings + retail.",
    blurb:
      "A preset, not a platform: the appointment is a bookings slot with a posted price, the shelf is retail, and the product sold at the chair attaches to the appointment's settlement — one visit, one payment. Salons, barbers, spas, groomers, and tattoo studios run this recipe; the shop that grows a product line just uses more retail, same tenant.",
    capabilities: [
      "Composes: bookings + retail",
      "Per-staff calendars, posted-price offerings (bookings)",
      "The product shelf (retail)",
      "Product orders attach to the appointment settlement",
    ],
    posture: "vendor",
    status: "preset",
    icon: "ContentCut",
    replaces: ["Vagaro", "Booksy", "Fresha"],
    agentCall: "booking_hold({ serviceId, slot })",
  },
  {
    slug: "fitness",
    name: "Fitness Studio",
    tagline: "Sell capacity-capped classes — a preset composing bookings + retail.",
    blurb:
      "A preset, not a platform: a class is a recurring bookings capacity-slot (18 mats = 18 bookings, full is a normal state), and the class pack is a prepaid retail item redeemed against future slots — never membership billing. Gyms, yoga and pilates studios, dojos, and dance schools run this recipe; merch rides any checkout.",
    capabilities: [
      "Composes: bookings + retail",
      "Recurring class schedule with hard capacity (bookings)",
      "Class packs + merch as items (retail)",
      "Pack credit redeems against future slots",
    ],
    posture: "vendor",
    status: "preset",
    icon: "FitnessCenter",
    replaces: ["Mindbody", "Glofox"],
    agentCall: "availability_get({ providerId, serviceId, from, to })",
  },
  {
    slug: "lodging",
    name: "Lodging",
    tagline: "Sell the stay — a preset composing bookings + rentals + retail.",
    blurb:
      "A preset, not a platform: the night is a bookings slot, the room is a rentals asset (deposit hold, condition tracked — the guest's check-in is the rentals checkout), and the minibar is retail bound to the stay. Hotels, BnBs, vacation rentals, hostels, and campgrounds run this recipe; one folio settles it.",
    capabilities: [
      "Composes: bookings + rentals + retail",
      "Nightly slots at posted rates (bookings)",
      "The room as a deposit-held, condition-tracked asset (rentals)",
      "Add-ons bound to the stay (retail)",
    ],
    posture: "vendor",
    status: "preset",
    icon: "Hotel",
    replaces: ["Cloudbeds", "Guesty", "Lodgify"],
    agentCall: "rental_checkout({ assetId, start, end })",
  },
  {
    slug: "courses",
    name: "Courses & Coaching",
    tagline: "Sell learning — a preset composing bookings + digital.",
    blurb:
      "A preset, not a platform: enrollment is one purchase that issues the digital entitlement (the recorded material, verifiable for its term) AND books the live sessions (bookings slots). Course creators, coaches, tutors, and cohort programs run this recipe; a cohort is a dated purchase, not a subscription, and re-downloads are entitlement checks, not support tickets.",
    capabilities: [
      "Composes: bookings + digital",
      "Live sessions as capacity-capped slots (bookings)",
      "The material as a term-bound entitlement (digital)",
      "One enrollment settles both",
    ],
    posture: "vendor",
    status: "preset",
    icon: "School",
    replaces: ["Teachable", "Kajabi", "Thinkific"],
    agentCall: "entitlement_issue({ sku, term: '1y' })",
  },
  {
    slug: "repair",
    name: "Repair & Trades",
    tagline: "Sell the scoped job plus its parts — a preset composing services + retail.",
    blurb:
      "A preset, not a platform: the job is a services engagement (quote → scope → milestones — you can't price it before you've seen it), and every part consumed is a retail order attached to the milestone that used it. Auto and phone repair, plumbers, electricians, and HVAC run this recipe; the invoice is labor plus goods on one job, and a mid-job surprise is a quote revision, not a silent overrun.",
    capabilities: [
      "Composes: services + retail",
      "Scope-before-price quoting + milestones (services)",
      "The parts shelf as real inventory (retail)",
      "Parts orders attach to the milestone that consumed them",
    ],
    posture: "vendor",
    status: "preset",
    icon: "Build",
    replaces: ["Jobber", "Housecall Pro", "ServiceTitan"],
    agentCall: "quote_submit({ requestId, lineItems, total })",
  },
  {
    slug: "creative-studio",
    name: "Creative Studio",
    tagline: "Sell scoped work delivered as a license — a preset composing services + digital.",
    blurb:
      "A preset, not a platform: the engagement is services (scope-before-price, paid by milestone) and the deliverable is a digital entitlement issued on final-milestone completion — delivery and settlement close together, and the client's usage rights live on the entitlement, verifiable later. Photographers, videographers, designers, and agencies run this recipe.",
    capabilities: [
      "Composes: services + digital",
      "Scoped engagements with milestone settlement (services)",
      "Deliverables as licensed entitlements (digital)",
      "Usage rights are first-class license terms",
    ],
    posture: "vendor",
    status: "preset",
    icon: "PhotoCamera",
    replaces: ["HoneyBook", "Pixieset", "Dubsado"],
    agentCall: "quote_submit({ requestId, lineItems, total })",
  },
  {
    slug: "equipment-delivery",
    name: "Equipment Hire & Delivery",
    tagline: "Sell delivered use of an asset — a preset composing rentals + shipping.",
    blurb:
      "A preset, not a platform: rentals owns the span (calendar, deposit, condition) and shipping runs the legs — outbound at checkout, collection at the span's end, with checkin and deposit release only when the asset is back. Party and event rentals, construction and medical equipment hire run this recipe; the customer never visits a yard.",
    capabilities: [
      "Composes: rentals + shipping",
      "Assets by the span with deposits (rentals)",
      "Outbound + collection legs bound to the round-trip (shipping)",
      "Zones gate what's deliverable",
    ],
    posture: "vendor",
    status: "preset",
    icon: "LocalShipping",
    replaces: ["Booqable", "Quipli"],
    agentCall: "rental_checkout({ assetId, start, end })",
  },
  {
    slug: "marketplace",
    name: "Marketplace",
    tagline: "Host other vendors — a preset composing retail + the platform-platform.",
    blurb:
      "A preset, not a platform: a marketplace is a tenant that is also a manager — its own retail face out front, and behind it a manager branch whose admitted vendors appear as its catalog node's children. The admittance bar is the product: the trust your buyers inherit is the bar your vendors cleared. Craft malls, curated multi-vendor shops, and consignment collectives run this recipe; the manager-branch step is operator-assisted today, with the self-serve door on the roadmap.",
    capabilities: [
      "Composes: retail + the platform-platform (federation spine)",
      "Your storefront face + cross-vendor catalog search (retail)",
      "Zero-trust admittance bar for who may list (federation)",
      "Admitted vendors as catalog children, each with its own shop",
    ],
    posture: "vendor",
    status: "preset",
    icon: "Storefront",
    replaces: ["Amazon Marketplace", "Etsy", "Faire"],
    agentCall: "federation_create_tenant({ manager_handle, handle, name })  // → <manager_handle>-<handle>",
  },

  /* ── CUSTOMER platforms — TOOLS you use to run the business ──────────── */
  {
    slug: "messaging",
    name: "Messaging",
    tagline: "Stay in touch — email, SMS, and DM as one platform.",
    blurb:
      "Transactional and lifecycle messaging across channels as a platform a tenant runs — email is the first channel live; SMS and DM follow (CO 123: email is a channel, not a platform). Deliverability, templates, and sending economics handled by the OS. Same tenant identity, same Com meter, same proof substrate as every other platform.",
    capabilities: [
      "Transactional + lifecycle sends",
      "Email live; SMS + DM coming",
      "Deliverability + reputation handling",
      "Agent-callable send API",
    ],
    posture: "customer",
    status: "available",
    icon: "Email",
    replaces: ["Klaviyo", "SendGrid", "Twilio"],
    agentCall: "email_send({ to: customer, template: 'order_shipped' })",
  },
  {
    slug: "shipping",
    name: "Shipping",
    tagline: "Rates, labels, and tracking as an OS capability.",
    blurb:
      "Multi-carrier rates, label generation, and tracking exposed as a platform — not a plugin you reconcile against. One tenant, one federation surface; agents can quote and buy shipping the same way they shop catalog.",
    capabilities: [
      "Multi-carrier rate shopping",
      "Label generation",
      "Tracking + delivery events",
      "Agent-callable rate + buy API",
    ],
    posture: "customer",
    status: "available",
    icon: "LocalShipping",
    replaces: ["ShipStation", "EasyPost", "Shippo"],
    agentCall: "shipping.rates({ orderId, to: address })",
  },
  {
    slug: "marketing",
    name: "Marketing",
    tagline: "Segments, consent-gated campaigns, A/B, and a send calendar.",
    blurb:
      "Human-facing marketing as an OS capability: rule-based segments over your party records, campaigns that execute through the consent-gated send path — recipient consent adjudicated on every send, exclusions recorded, never bypassed — deterministic A/B splits with per-variant results from real send events, and a calendar that rides tenant workflows. Agent-facing reach stays composed from the Offer Bus and Reputation (CO 172).",
    capabilities: [
      "Rule-based segments with consent-aware preview",
      "Campaigns through the consent-gated send path",
      "Deterministic A/B splits, per-variant reporting",
      "Send calendar riding tenant workflows",
    ],
    posture: "customer",
    status: "available",
    icon: "Campaign",
    replaces: ["HubSpot", "ActiveCampaign", "Customer.io"],
    agentCall: "marketing_campaign_execute({ campaign_id })",
  },
  {
    slug: "customer-service",
    name: "Customer Service",
    tagline: "Care for buyers — tickets, responses, resolution.",
    blurb:
      "Support tickets, responses, and resolution across everything you sell — one surface, regardless of which platforms a tenant composed. Shares the tenant's brand voice and knowledge so answers are consistent and grounded.",
    capabilities: [
      "Tickets + conversation threads",
      "Brand-voice + knowledge-grounded replies",
      "Agent-callable support API",
      "Resolution + escalation tracking",
    ],
    posture: "customer",
    status: "available",
    icon: "SupportAgent",
    replaces: ["Zendesk", "Intercom", "Gorgias"],
    agentCall: "support.ticket.create({ customerId, subject, body })",
  },
  {
    slug: "reviews",
    name: "Reviews",
    tagline: "Collect, summarize, and moderate buyer reviews.",
    blurb:
      "Product and vendor reviews as a platform — collection, summaries, and moderation across everything a tenant sells. Grounded in real orders, surfaced to humans and agents alike, under the tenant's own brand voice.",
    capabilities: [
      "Review collection + threads",
      "Summaries across a catalog",
      "Moderation",
      "Agent-callable reviews API",
    ],
    posture: "customer",
    status: "available",
    icon: "Star",
    replaces: ["Yotpo", "Judge.me", "Trustpilot"],
    agentCall: "reviews_summary({ productId })",
  },
  {
    slug: "tax",
    name: "Tax",
    tagline: "Sales tax, VAT, and nexus — calculated, not configured.",
    blurb:
      "Sales-tax (US, ZIP-level), VAT (EU, per-country), and economic-nexus tracking as a composable platform any vendor platform's checkout calls. The one singleton: rates are law, shared by the whole network, never a per-tenant configuration. A compliance helper — it calculates and flags nexus; it does not file or remit.",
    capabilities: [
      "Cart tax calculation (the generalized checkout seam)",
      "US ZIP-level rates + EU VAT",
      "Economic-nexus tracking per state",
      "Agent-callable calculate + nexus API",
    ],
    posture: "customer",
    status: "available",
    icon: "Receipt",
    replaces: ["TaxJar", "Avalara", "Vertex"],
    agentCall: "tax_calculate({ items, shipping_address: { state, zip_code } })",
  },
  {
    slug: "analytics",
    name: "Analytics",
    tagline: "How the business is doing — one surface, already wired.",
    blurb:
      "Revenue, orders, AOV, CLV, churn-risk, funnels, and usage as a composable read surface that already knows the tenant's catalog, customers, and orders. v1 surfaces the insight ComOS already produces internally — no CSV exports between disconnected dashboards.",
    capabilities: [
      "Revenue / orders / AOV rollups",
      "CLV + churn-risk segments",
      "Funnels + conversion",
      "Agent-callable read API",
    ],
    posture: "customer",
    status: "available",
    icon: "Insights",
    replaces: ["Looker", "Mixpanel", "Amplitude"],
    agentCall: "analytics_overview({})",
  },
  {
    slug: "reputation-attestation",
    name: "Reputation",
    tagline: "A track record an agent can check — written by settlement.",
    blurb:
      "A track record an agent counterparty reads before it transacts — settled and refund counts. Only a settlement the hub executed can add to it, never the tenant, and every such event sits on a public hash-chained log a counterparty can verify without credentials. In an agent-native market, a checkable record replaces the brand.",
    capabilities: [
      "Settlement-derived track record",
      "Written only by settlement, never by the tenant",
      "Public hash-chained log, verifiable without credentials",
      "Read any counterparty before transacting",
      "Agent-callable track-record API",
    ],
    posture: "customer",
    status: "available",
    icon: "VerifiedUser",
    replaces: ["Trustpilot (review-gaming)", "Stripe Radar trust signals"],
    agentCall: "reputation_get({ tenant_id })",
  },
  {
    slug: "offer-bus",
    name: "Offer Bus",
    tagline: "Permissioned offers — the agent opted in, or it never arrives.",
    blurb:
      "A permission-based offer broadcast bus: a publisher puts a structured offer on the bus, and it reaches only subscribers who already granted that publisher permission. There is no broadcast-to-all — spam is structurally impossible. The consumer agent is in control. The agent-native replacement for advertising.",
    capabilities: [
      "Structured offer publish",
      "Permission-gated delivery (opt-in only)",
      "Subscribe / unsubscribe as free consent acts",
      "Agent-callable publish + browse API",
    ],
    posture: "customer",
    status: "available",
    icon: "Campaign",
    replaces: ["Ad networks", "Email blast tools", "Push-notification spam"],
    agentCall: "offer_list({})",
  },
  {
    slug: "loyalty",
    name: "Loyalty",
    tagline: "Points, tiers, redemption — across everything you sell.",
    blurb:
      "A loyalty program a tenant composes to reward its customers — points, tiers, and redemption — across whatever it sells. The ledger is the source of truth; balance is derived, accrual is idempotent, redemption fails clean. Same tenant identity, same Com meter as every platform.",
    capabilities: [
      "Points balance + tier progress",
      "Accrue (idempotency-keyed) + redeem",
      "Redemption options catalog",
      "Agent-callable balance + redeem API",
    ],
    posture: "customer",
    status: "available",
    icon: "Loyalty",
    replaces: ["Smile.io", "LoyaltyLion", "Yotpo Loyalty"],
    agentCall: "loyalty_check_balance({ customer_id })",
  },
  {
    slug: "workflows",
    name: "Workflows",
    tagline: "Author multi-step automations your agents and tools execute.",
    blurb:
      "Declarative, tenant-owned automations: one to ten ordered steps over the platform tools you composed, plus priced research and authoring steps — run manually or on an hourly-floor cron. Every step is metered and priced up front, so an automation's cost is known before it runs.",
    capabilities: [
      "Ordered steps over your composed platform tools",
      "Priced research + authoring steps",
      "Manual runs or hourly-floor cron",
      "Agent-callable workflow API",
    ],
    posture: "customer",
    status: "available",
    icon: "AccountTree",
    replaces: ["Zapier", "Make", "n8n"],
    agentCall: "workflow_create({ name, steps })",
  },
  {
    slug: "consent",
    name: "Consent",
    tagline: "The consent ledger — record, prove, and withdraw, GDPR-shaped.",
    blurb:
      "A cookie and tracking consent ledger a tenant composes: the current session's consent state, the full decision trail (GDPR Art. 7(1)), and withdrawal as a first-class act (Art. 7(3)) — an append-only record you can prove from, not a banner you hope was clicked.",
    capabilities: [
      "Session consent state",
      "Append-only decision trail (GDPR Art. 7(1))",
      "Withdrawal as a first-class act (Art. 7(3))",
      "Agent-callable consent API",
    ],
    posture: "customer",
    status: "available",
    icon: "FactCheck",
    replaces: ["OneTrust", "Cookiebot", "Usercentrics"],
    agentCall: "consent_get({ session_id })",
  },
  {
    slug: "recommendations",
    name: "Recommendations",
    tagline: "Similar, trending, and for-you picks across your catalog.",
    blurb:
      "Product recommendations as a composable platform — similar items, trending, per-customer picks, interaction tracking, and index rebuilds, grounded in the tenant's own catalog and orders rather than a third-party pixel.",
    capabilities: [
      "Similar-item + trending surfaces",
      "Per-customer picks",
      "Interaction tracking + index rebuild",
      "Agent-callable recommendations API",
    ],
    posture: "customer",
    status: "available",
    icon: "Recommend",
    replaces: ["Nosto", "Algolia Recommend", "Clerk.io"],
    agentCall: "recommendations_similar({ product_id })",
  },
  {
    slug: "integration",
    name: "Integration",
    tagline: "Connect the outside world — connectors, syncs, status.",
    blurb:
      "The connector market a tenant composes to link ComOS to external systems — commerce platforms, ERPs, POS, payment gateways — and run syncs, under one OS surface. One connector capability, composed once, feeding whatever platforms a tenant runs, instead of N bespoke integrations.",
    capabilities: [
      "Browse + connect available connectors",
      "Trigger + monitor syncs",
      "Connection + last-sync status",
      "Agent-callable connect + sync API",
    ],
    posture: "customer",
    status: "available",
    icon: "Hub",
    replaces: ["Zapier", "Celigo", "MuleSoft"],
    agentCall: "integration_list_available({})",
  },
];

export function getPlatform(slug: string): Platform | undefined {
  return PLATFORMS.find((p) => p.slug === slug);
}

// CO 109 posture split — the two relationships a tenant can have to a platform.
// VENDOR = a kind of business you become / sell on (the non-retail visitor finds
// itself here). CUSTOMER = a tool you use to run the business.
export const VENDOR_PLATFORMS = PLATFORMS.filter((p) => p.posture === "vendor");
export const CUSTOMER_PLATFORMS = PLATFORMS.filter(
  (p) => p.posture === "customer",
);

/**
 * The tenant-core — the actual spine. Every tenant carries these regardless of
 * which platforms it composes. (CO 071)
 */
export interface TenantCore {
  name: string;
  blurb: string;
}

export const TENANT_CORE: TenantCore[] = [
  {
    name: "Identity",
    blurb:
      "One tenant identity and a federation endpoint every agent can reach.",
  },
  {
    name: "Accounts",
    blurb:
      "A Com balance and a dollar account — the financial substrate your business runs on.",
  },
  {
    name: "Agents & workflows",
    blurb: "An autonomous fleet that gets, combines, and acts on your behalf.",
  },
  {
    name: "Governance",
    blurb:
      "Autonomy levels, audit trail, and rollback on every action — control by default.",
  },
  {
    name: "Brand voice",
    blurb:
      "Your voice, applied consistently across every platform you compose.",
  },
  {
    name: "Knowledge",
    blurb:
      "Your retrievable knowledge (RAG), available to every agent you run.",
  },
];
